<template>
  <NTable
    v-if="orderItems.length"
    :data="orderItems"
    identifier="orderId"
    cell-padding="small"
    :column-splitter="false"
    :striped="false"
    fixed-header
    hover-alignment="right"
    hover-offset="15%"
    class="rounded-none border-none"
    @click.stop
    @row-click="(row) => updateActiveIsin(row.instrumentIdentification)"
  >
    <template #hoveredElements="{ row }">
      <div class="flex items-center h-full gap-2 pe-24 ps-8">
        <template v-if="isOpenOrder(row) && !isPendingOrder(row)">
          <template v-if="!isAlgorithmOrder(row)">
            <NegarOrderPositionIcon
              v-gid="{
                module: 'order',
                screen: 'home',
                section: 'ordersTable',
                component: 'orderPositionIcon',
              }"
              size="20"
              @click.stop="handleOrderPositionDialog(row, $event.target)"
            />

            <NegarEditIcon
              v-gid="{
                module: 'order',
                screen: 'home',
                section: 'ordersTable',
                component: 'editIcon',
              }"
              size="20"
              class="mx-2"
              @click.stop="handleEdit(row)"
            />
          </template>

          <NegarTrashLightIcon
            v-gid="{
              module: 'order',
              screen: 'home',
              section: 'ordersTable',
              component: 'trashIcon',
            }"
            size="20"
            class="text-critical-primary ml-2"
            @click.stop="handleDelete(row, $event.target)"
          />
        </template>

        <NegarInfoWithBorderIcon
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'ordersTable',
            component: 'infoIcon',
          }"
          size="20"
          @click.stop="orderInfoDialog(row)"
        />
      </div>
    </template>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.order.order_type')"
      prop="orderType"
      sortable
      align="text-right"
    >
      <NText
        v-if="row.orderSideId === ORDER_SIDES.buy"
        variant="caption2"
        weight="medium"
        class="text-brand-primary"
      >
        {{ t('common.buy') }}
      </NText>
      <NText
        v-else
        variant="caption2"
        weight="medium"
        class="text-critical-primary"
      >
        {{ t('common.sell') }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.symbol')"
      prop="instrumentName"
      class="flex"
      sortable
      align="text-right"
    >
      <NText :key="row.instrumentName" variant="caption2" weight="medium">
        {{ row.instrumentName }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="`${t('modules.portfolio.quantity')} (${t(
        'modules.order.executed'
      )})`"
      prop="quantity"
      sortable
      align="text-right"
    >
      <template v-if="row.orderType === OrderTypeEnum.SLTP">
        <template v-if="row.targetQuantity">
          <NumberFormatter #="{ number }" :value="row.targetQuantity">
            <ChangePriceEffect
              :key="row.targetQuantity"
              :price="row.targetQuantity"
            >
              <NText variant="caption2" weight="medium">
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.executedQuantity">
            <NText variant="caption2" weight="medium"> ({{ number }}) </NText>
          </NumberFormatter>
        </template>

        <NText v-else> - </NText>
      </template>

      <NText
        v-else-if="
          row.orderType === OrderTypeEnum.Conditional && row.investValue
        "
      >
        -
      </NText>

      <template v-else>
        <NumberFormatter #="{ number }" :value="row.quantity">
          <ChangePriceEffect :key="row.quantity" :price="row.quantity">
            <NText variant="caption2" weight="medium">
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>

        <NumberFormatter #="{ number }" :value="row.executedQuantity">
          <NText variant="caption2" weight="medium"> ({{ number }}) </NText>
        </NumberFormatter>
      </template>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.order.order_price')"
      prop="price"
      sortable
      align="text-right"
    >
      <NText
        v-if="row.orderType === OrderTypeEnum.Conditional"
        variant="overline"
        class="text-neutral-secondary me-1"
      >
        {{ row.upperThreshold ? t('common.lowerThan') : t('common.upperThan') }}
      </NText>

      <NText
        v-if="
          isOfflineMarketOrder(row.orderExecutionType) ||
          row.orderType === OrderTypeEnum.SLTP
        "
        variant="caption2"
        weight="medium"
      >
        {{ t('modules.order.priceType.marketPrice.title') }}
      </NText>

      <NumberFormatter v-else #="{ number }" :value="getOrderPrice(row)" round>
        <NText :key="row.price" dir="ltr" variant="caption2" weight="medium">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.current_price')"
      prop="lastPrice"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.lastTradePrice" round>
        <NText
          :key="row.lastTradePrice"
          dir="ltr"
          variant="caption2"
          weight="medium"
        >
          <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
          {{ row.lastTradePrice ? number : '' }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.order.time')"
      prop="entryDate"
      sortable
      align="text-right"
    >
      <NText :key="row.entryDate" variant="caption2" weight="medium">
        {{ creationTime(row.entryDate) }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.order.order_state')"
      prop="state"
      sortable
      align="text-right"
    >
      <div :key="row.orderState" class="flex justify-between items-center">
        <NText variant="caption2" weight="medium">
          {{ row.orderState }}
        </NText>

        <div
          v-if="row.orderType === OrderTypeEnum.Offline"
          class="flex items-center py-1 pl-2 pr-1 rounded-3xl bg-purple-primary-disable h-6"
        >
          <img
            src="@/assets/images/moon.svg"
            alt="offline_order"
            width="16"
            height="16"
            class="me-1"
          />

          <NText variant="overline" weight="medium" class="text-purple-primary">
            {{ t('orders.twentyFourHour') }}
          </NText>
        </div>

        <div
          v-if="row.orderType === OrderTypeEnum.Conditional"
          class="flex items-center px-3 rounded-3xl bg-neutral-secondary-press text-neutral-quaternary h-6"
        >
          <NText
            variant="overline"
            weight="medium"
            class="text-neutral-secondary-hover"
          >
            {{ t('orders.conditional.title') }}
          </NText>
        </div>

        <div
          v-if="row.orderType === OrderTypeEnum.SLTP"
          class="flex items-center px-3 rounded-3xl bg-neutral-secondary-press text-neutral-quaternary h-6"
        >
          <NText
            variant="overline"
            weight="medium"
            class="text-neutral-secondary-hover"
          >
            {{ t('modules.order.SLTP') }}
          </NText>
        </div>
      </div>
    </NTableColumn>
  </NTable>

  <EmptyList
    v-else
    :title="EMPTY_ORDER_MESSAGE.title[filter]"
    :body="EMPTY_ORDER_MESSAGE.message[filter]"
    class="h-full justify-center items-center grid overflow-auto p-3"
    title-class="mt-3"
    @click.stop
  >
    <template #image>
      <Image
        src="empty_box"
        alt="not_found"
        class="w-28 h-max justify-self-center"
      />
    </template>

    <router-link
      v-slot="{ navigate }"
      :to="emptyStateRoutePath[filter]"
      class="flex justify-center"
    >
      <NButton
        v-gid="{
          module: 'order',
          screen: 'home',
          section: 'ordersTable',
          component: `${filter}OrdersHistoryNavigateButton`,
        }"
        variant="flat"
        size="small"
        class="text-c1 font-bold"
        @click="navigate"
      >
        {{ t('orders.seeOrdersHistory', { orderType }) }}
      </NButton>
    </router-link>
  </EmptyList>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { NText } from '@negar/core';
import {
  NegarTrashLightIcon,
  NegarInfoWithBorderIcon,
  NegarEditIcon,
  NegarOrderPositionIcon,
} from '@negar/icons';
import {
  ORDER_SIDES,
  EMPTY_ORDER_MESSAGE,
  getOrderSideValue,
  isOpenOrder,
  isPendingOrder,
  ORDER_FILTERS,
  isAlgorithmOrder,
  getOrderPrice,
  isOfflineMarketOrder,
} from '@core/order';
import {
  InitialPublicOfferItem,
  OrderItem,
  OrderTypeEnum,
} from '@libs/api-reference';
import { isOptionSymbol } from '@core/symbol';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import EmptyList from '@/components/EmptyList.vue';
import Image from '@/components/Image.vue';

import { useIpoDialogs } from '@/modules/ipo';
import { SLTP_DETAIL, useOrderDialogs } from '@/modules/order';
import { useActiveSymbolIsin } from '@/modules/symbol';

const emit = defineEmits<{
  (e: 'deleteOrder', value: { selectedOrder: OrderItem }): void;
  (e: 'deleteOfflineOrder', value: { selectedOrder: OrderItem }): void;
}>();

const props = defineProps<{
  orderItems: OrderItem[];
  filter: string;
  loadingDeleteOrder: boolean;
  offers?: InitialPublicOfferItem[];
}>();

const { t, d } = useI18n();

const { openDeleteOfferDialog, openEditOfferDialog } = useIpoDialogs();

const selectedOrder = ref<OrderItem | null>();

const {
  openEditOrderFormDialog,
  openEditOptionOrderFormDialog,
  openDeleteOrderDialog,
  openOfflineOrderInfoDialog,
  openOrderInfoDialog,
  openOrderPositionDialog,
  openConditionalOrderInfoDialog,
  openSLTPInfoDialog,
} = useOrderDialogs();
const { updateActiveIsin } = useActiveSymbolIsin();

const ORDER_DETAIL_DIALOGS = {
  [OrderTypeEnum.OMS]: openOrderInfoDialog,
  [OrderTypeEnum.Offline]: openOfflineOrderInfoDialog,
  [OrderTypeEnum.Conditional]: openConditionalOrderInfoDialog,
  [OrderTypeEnum.SLTP]: openSLTPInfoDialog,
};

const orderType = computed(() =>
  props.filter === ORDER_FILTERS.open.value
    ? ''
    : ORDER_FILTERS[props.filter].label
);

const creationTime = (date) =>
  `${d(date, 'shortDate')} ,
    ${d(date, 'shortTime')}`;

const deleteOrder = (order: { selectedOrder: OrderItem }) => {
  if (isAlgorithmOrder(order.selectedOrder)) {
    emit('deleteOfflineOrder', order);
  } else {
    emit('deleteOrder', order);
  }
};

const handleDeleteOrderDialog = (
  order: OrderItem,
  deleteOrderButtonRef: HTMLElement
) => {
  selectedOrder.value = order;

  openDeleteOrderDialog({
    order,
    handleDelete: deleteOrder,
    referenceElementRef: deleteOrderButtonRef,
    loading: props.loadingDeleteOrder,
    afterClose: closeDeleteOrderDialog,
  });
};

const closeDeleteOrderDialog = () => {
  selectedOrder.value = null;
};

const orderInfoDialog = (order: OrderItem) => {
  selectedOrder.value = order;

  const offer = props.offers?.find(
    (offer) => offer.instrumentIsin === order.instrumentIdentification
  );

  const orderType =
    !order?.isAlgorithm && !isAlgorithmOrder(order)
      ? OrderTypeEnum.OMS
      : order.orderType; // After the `orderType` data is added to the socket, We should only use orderType as before

  ORDER_DETAIL_DIALOGS[orderType]({
    afterClose: closeDeleteOrderDialog,
    offer,
    order,
    from: SLTP_DETAIL.order,
    instrumentName: order.instrumentName,
  });
};

const handleOrderPositionDialog = (
  order: OrderItem,
  orderPositionIconRef: HTMLElement
) => {
  if (order.exchangeCode) {
    openOrderPositionDialog({
      order,
      referenceElementRef: orderPositionIconRef,
    });
  }
};

const handleEdit = (order: OrderItem) => {
  const offer = props.offers?.find(
    (offer) => offer.instrumentIsin === order.instrumentIdentification
  );

  if (offer) {
    openEditOfferDialog({
      offer,
      orderId: order.orderId,
    });
  } else if (isOptionSymbol(order.instrumentTypeCode)) {
    openEditOptionOrderFormDialog({
      type: getOrderSideValue(order.orderSideId),
      selectedOrder: order,
    });
  } else {
    openEditOrderFormDialog({
      type: getOrderSideValue(order.orderSideId),
      selectedOrder: order,
    });
  }
};

const handleDelete = (row: OrderItem, deleteOrderButtonRef: HTMLElement) => {
  const offer = props.offers?.find(
    (offer) => offer.instrumentIsin === row.instrumentIdentification
  );

  if (offer) {
    openDeleteOfferDialog({
      offer,
      orderId: row.orderId,
    });
  } else {
    handleDeleteOrderDialog(row, deleteOrderButtonRef);
  }
};

const emptyStateRoutePath = {
  [ORDER_FILTERS.all.value]: '/orders-history',
  [ORDER_FILTERS.open.value]: '/orders-history',
  [ORDER_FILTERS.failed
    .value]: `/orders-history?${ORDER_FILTERS.failed.value}=true`,
  [ORDER_FILTERS.done
    .value]: `/orders-history?${ORDER_FILTERS.done.value}=true`,
};
</script>
